import React, { useState, useEffect, useCallback } from 'react'
import {
  EditorState,
  ContentState,
  convertToRaw,
  convertFromRaw,
  convertFromHTML,
  DraftHandleValue,
  Modifier,
} from 'draft-js'
import dynamic from 'next/dynamic'
import { getDefaultValidations, validate } from '../Validations'
import { useTheme } from '@libs/useTheme'
import Box from '@components/base/Box'
import { BaseInputProps } from '../BaseInputProps'
import Resizeable from '@libs/Utility/resizeable'
import Tooltip from '@components/tootilp/Tooltip'
import { MdOutlineLink, MdOutlineLinkOff } from 'react-icons/md'
import { debounce, isJSON } from '@libs/Utility/util'
import draftToHtml from 'draftjs-to-html'
import { useRouter } from 'next/router'
import { useOrganisationProfile } from '@libs/useOrganisationProfile'
import { useAuth } from '@libs/Auth/useAuth'
import { EditorFullCustomDecorators, PlaceholderButton } from './TextEditorUtil'

const DynamicEditor = dynamic<any>(
  () => import('react-draft-wysiwyg').then((mod) => mod.Editor as any),
  { ssr: false }
)

const RichTextEditor: React.FC<
  BaseInputProps & {
    setError?: (message: string, testId: string) => void
    htmlOutput?: boolean
    resizeEnabled?: boolean
    decorators?: any[]
  }
> = ({
  label,
  placeholder,
  className,
  validations,
  required,
  hint,
  testid,
  onChange,
  value,
  show = true,
  textColour,
  labelBgColour,
  tooltip,
  setError,
  htmlOutput = false,
  resizeEnabled = true,
  decorators,
}) => {
  // console.log({ value })
  const { theme } = useTheme()
  const { isReady } = useRouter()
  const [error, setLocalError] = useState('')

  const [showTooltip, setShowTooltip] = useState(true)
  useEffect(() => {
    // Autohide tooltip if the input is not required.
    if (tooltip?.toLowerCase().includes('require') && !required)
      setShowTooltip(false)
  }, [tooltip, required])

  const backgroundColour = theme.editorBackgroundColour

  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  )

  useEffect(() => {
    // console.log({ value, valueType: typeof value, window: !!window, isReady })
    if (!isReady) return
    if (value && typeof value === 'string' && value.trim() !== '' && !!window) {
      try {
        if (htmlOutput && !isJSON(value)) {
          console.log('HTML', { value })
          const rawContentFromProps = convertFromHTML(value)
          const contentState = ContentState.createFromBlockArray(
            rawContentFromProps.contentBlocks,
            rawContentFromProps.entityMap
          )
          setEditorState(EditorState.createWithContent(contentState))
        } else {
          console.log('Not HTML', { value })
          const rawContentFromProps = JSON.parse(value)
          const contentState = convertFromRaw(rawContentFromProps)
          setEditorState(EditorState.createWithContent(contentState))
        }
      } catch (error) {
        console.error(
          'Error to convert the initial value to EditorState: ',
          error
        )
      }
    }
  }, [isReady, window])

  const handleChange = useCallback(
    debounce((val: string) => {
      if (onChange) {
        onChange(val)
      }
    }, 1000),
    []
  )

  if (!show) return <></>

  const parsedValidations = getDefaultValidations(undefined, validations)

  const onEditorStateChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState)

    const plainText = newEditorState.getCurrentContent().getPlainText('')

    const errorMessage = validate(plainText, parsedValidations)

    if (!!setError) setError(errorMessage, testid ?? label ?? 'unknown')

    setLocalError(errorMessage)

    if (!errorMessage) {
      const rawContentState = convertToRaw(newEditorState.getCurrentContent())
      let payload

      htmlOutput
        ? (payload = {
            data: JSON.stringify(rawContentState),
            markup: draftToHtml(rawContentState),
          })
        : (payload = JSON.stringify(rawContentState))

      handleChange(payload)
    }
  }

  const isDarkTheme = theme.type === 'DARK'

  // const handleKeyCommand = (cmd: string): DraftHandleValue => {
  //   console.log({ cmd })
  //   if (cmd === 'init-autocomplete') {
  //     return 'handled'
  //   }
  //   return 'not-handled'
  // }
  //

  // console.log({ decorators })

  return (
    <label
      className={['flex flex-col my-4', className].join(' ')}
      style={{ color: textColour }}
    >
      {label && (
        <span
          className={`absolute text-sm mb-2 ml-2 z-10 ${backgroundColour} ${
            required ? 'pl-2' : 'px-2'
          } transition-all ${error ? 'text-red-500' : ''}`}
          style={{
            background: backgroundColour,
            transform: 'translateY(-50%)',
          }}
        >
          <Tooltip
            show={showTooltip}
            tooltip={tooltip}
            className={`flex flex-col`}
          >
            <span
              className={`z-10 ${
                required
                  ? "after:content-['*'] after:mx-2 after:text-red-500"
                  : ''
              }`}
            >
              {label}
            </span>
          </Tooltip>
        </span>
      )}
      {typeof window === 'object' && (
        <Resizeable
          enabled={resizeEnabled}
          className={resizeEnabled ? '' : className}
          initalSize={{ height: 200 }}
        >
          <DynamicEditor
            spellCheck
            editorState={editorState}
            onEditorStateChange={onEditorStateChange}
            wrapperClassName={`${resizeEnabled ? 'size-full' : 'w-full flex-1'} flex flex-col !box-border`}
            /* @ts-ignore */
            // keyBindingFn={keyBindingFn}
            // handleKeyCommand={handleKeyCommand}
            editorClassName={`my-rich-editor ${
              error ? 'text-red-500 border-red-500' : ''
            }`}
            toolbarCustomButtons={
              htmlOutput
                ? [
                    <PlaceholderButton
                      key="placeholderButton"
                      editorState={editorState}
                      onChange={setEditorState}
                      type={decorators ? 'membership' : undefined}
                    />,
                  ]
                : []
            }
            toolbarClassName={`flex flex-wrap justify-center gap-2 p-2 border-b border-gray-300 ${
              isDarkTheme ? 'text-blue-700 ' : 'text-black'
            }`}
            toolbar={{
              options: [
                'fontFamily',
                'blockType',
                'fontSize',
                'inline',
                'colorPicker',
                'list',
                'textAlign',
                'remove',
                'history',
                'link',
              ],
              fontSize: {},
              blockType: {
                inDropdown: true,
                options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'],
              },
              inline: {
                options: [
                  'bold',
                  'italic',
                  'underline',
                  'strikethrough',
                  'monospace',
                  'superscript',
                  'subscript',
                ],
                bold: {},
                italic: {},
                underline: {},
                strikethrough: {},
                monospace: {},
                superscript: {},
                subscript: {},
              },
              list: {
                options: ['unordered', 'ordered'],
                unordered: {},
                ordered: {},
              },

              link: {
                inDropdown: false,
                className: undefined,
                component: undefined,
                popupClassName: undefined,
                dropdownClassName: undefined,
                showOpenOptionOnHover: true,
                defaultTargetOption: '_self',
                options: ['link', 'unlink'],
                // link: {
                //   icon: <MdOutlineLink />,
                //   className: 'fill-black text-black',
                // },
                // unlink: {
                //   icon: <MdOutlineLinkOff />,
                //   className: 'fill-black text-black',
                // },
                linkCallback: undefined,
              },
              textAlign: {
                options: ['left', 'center', 'right'],
                left: {},
                center: {},
                right: {},
              },

              remove: {},
              history: {
                options: ['undo', 'redo'],
                undo: {},
                redo: {},
              },
            }}
            customDecorators={decorators ?? EditorFullCustomDecorators}
            placeholder={placeholder}
            data-testid={testid ?? undefined}
            // mention={{
            //   separator: ' ',
            //   trigger: '@',
            //   suggestions: [
            //     { text: 'APPLE', value: 'apple', url: 'www.google.com' },
            //     { text: 'BANANA', value: 'banana', url: '' },
            //     { text: 'PEAR', value: 'pear' },
            //   ],
            //   mentionComponent: CustomMention,
            // }}
          />
        </Resizeable>
      )}
      {hint && <span className="mt-1 mb-4 text-xs">{hint}</span>}
      <Box
        ignoreTheme
        className={`mt-1 transition-all ${
          error ? 'opacity-100 text-red-500' : 'opacity-0'
        }`}
      >
        {error || '-'}
      </Box>
    </label>
  )
}

export default RichTextEditor
